import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Card,
  CardContent,
  IconButton,
  Divider,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Delete, Add, WhatsApp, Clear } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useQuote } from "../context/QuoteContext";
import { formatPrice, getAllProducts, productCategories } from "../data/products";
import { companyInfo } from "../data/products";
import SEO from "../components/SEO";

const QuoteContainer = styled(Container)(({ theme }) => ({
  marginTop: "2rem",
  marginBottom: "4rem",
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
}));

// Replace with your Google Apps Script Web App URL
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycby5vIeY_p6BntotP5PRi0w9vmYzwC55UwNCroKa5Rx4IVSOHo8zncRAprGVOQUQ9ZHcHg/exec';

const Inquiry = () => {
  const { quoteItems, removeFromQuote, clearQuote, addToQuote } = useQuote();
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    requirements: "",
  });
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitType, setSubmitType] = useState('');

  const allProducts = getAllProducts();
  const filteredProducts = selectedCategory 
    ? allProducts.filter(product => product.categoryId === parseInt(selectedCategory))
    : allProducts;

  const handleCustomerInfoChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleProductChange = (productId) => {
    setSelectedProduct(productId);
    if (productId) {
      const product = allProducts.find(p => p.id === parseInt(productId));
      if (product) {
        setQuantity(product.minOrder.toString());
      }
    } else {
      setQuantity("");
    }
  };

  const handleAddProductToQuote = () => {
    if (selectedProduct && quantity) {
      const product = allProducts.find(p => p.id === parseInt(selectedProduct));
      if (product) {
        addToQuote({
          productId: product.id,
          name: product.name,
          price: product.price,
          unit: product.unit,
          quantity: parseInt(quantity),
          notes: notes,
          categoryName: product.categoryName
        });
        
        setSelectedCategory("");
        setSelectedProduct("");
        setQuantity("");
        setNotes("");
      }
    }
  };

  const saveToGoogleSheets = async (data) => {
    try {
      //eslint-disable-next-line no-unused-vars
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'quote',
          ...data,
          quoteItems: quoteItems
        }),
        mode: 'no-cors'
      });
      return { success: true };
    } catch (error) {
      console.error('Error saving to Google Sheets:', error);
      return { success: false, error: error.message };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Save to Google Sheets
    const sheetResult = await saveToGoogleSheets(customerInfo);
    
    // Open WhatsApp with quote data
    handleWhatsAppQuote();
    
    // Show success message
    setTimeout(() => {
      setIsSubmitting(false);
      if (sheetResult.success) {
        setSubmitMessage('Quote request submitted successfully! We will get back to you within 24 hours. Your quote has also been sent via WhatsApp.');
        setSubmitType('success');
      } else {
        setSubmitMessage('Quote sent via WhatsApp! There was an issue saving to our database, but we received your quote request.');
        setSubmitType('warning');
      }
      
      clearQuote();
      setCustomerInfo({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        requirements: ''
      });
    }, 1000);
  };

  const calculateTotal = () => {
    return quoteItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleWhatsAppQuote = () => {
    let message = "Hi, I would like to request a quote for the following items:\n\n";
    
    quoteItems.forEach((item, index) => {
      message += `${index + 1}. ${item.name}\n`;
      message += `   Quantity: ${item.quantity} ${item.unit}\n`;
      message += `   Price: ${formatPrice(item.price)} per ${item.unit}\n`;
      if (item.notes) {
        message += `   Notes: ${item.notes}\n`;
      }
      message += '\n';
    });
    
    message += `Total Estimated Value: ${formatPrice(calculateTotal())}\n\n`;
    
    if (customerInfo.name) {
      message += `Customer Details:\n`;
      message += `Name: ${customerInfo.name}\n`;
      if (customerInfo.company) message += `Company: ${customerInfo.company}\n`;
      if (customerInfo.phone) message += `Phone: ${customerInfo.phone}\n`;
      if (customerInfo.email) message += `Email: ${customerInfo.email}\n`;
      message += '\n';
    }
    
    message += "Please provide your best quote and delivery timeline.";
    
    const whatsappUrl = `https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const selectedProductDetails = selectedProduct 
    ? allProducts.find(p => p.id === parseInt(selectedProduct))
    : null;

  return (
    <QuoteContainer maxWidth="lg">
      <SEO
        title="Get Quote - Shree Balaji Corporation"
        description="Request a quote for PET straps, bale covers, leather washers, and strapping machines for the cotton industry."
        keywords="get quote, request quote, PET straps, bale covers, Akola, Maharashtra"
        url="/inquiry"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          color: "white",
          textAlign: "center",
          py: 6,
          px: 4,
          borderRadius: 2,
          mb: 4,
        }}
      >
        <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
          Get Quote
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.9 }}>
          Request a customized quote for your requirements
        </Typography>
      </Box>

      {submitMessage && (
        <Alert severity={submitType} sx={{ mb: 3 }}>
          {submitMessage}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Quote Items Section */}
        <Grid item xs={12} lg={7}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <StyledPaper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  Quote Items
                </Typography>
                {quoteItems.length > 0 && (
                  <Button
                    startIcon={<Clear />}
                    onClick={clearQuote}
                    color="error"
                    variant="outlined"
                    size="small"
                    disabled={isSubmitting}
                  >
                    Clear All
                  </Button>
                )}
              </Box>

              {/* Add Product Section */}
              <Box sx={{ mb: 4, p: 3, backgroundColor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Add Products to Quote</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Select Category</InputLabel>
                      <Select
                        value={selectedCategory}
                        label="Select Category"
                        onChange={(e) => {
                          setSelectedCategory(e.target.value);
                          setSelectedProduct("");
                          setQuantity("");
                        }}
                        disabled={isSubmitting}
                      >
                        <MenuItem value="">All Categories</MenuItem>
                        {productCategories.map(category => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Select Product</InputLabel>
                      <Select
                        value={selectedProduct}
                        label="Select Product"
                        onChange={(e) => handleProductChange(e.target.value)}
                        disabled={(!selectedCategory && filteredProducts.length === 0) || isSubmitting}
                      >
                        {filteredProducts.map(product => (
                          <MenuItem key={product.id} value={product.id}>
                            {product.name} - {formatPrice(product.price)}/{product.unit}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      disabled={!selectedProduct || isSubmitting}
                      inputProps={{ 
                        min: selectedProductDetails?.minOrder || 1 
                      }}
                      helperText={selectedProductDetails ? `Minimum order: ${selectedProductDetails.minOrder} ${selectedProductDetails.unit}` : ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Notes (Optional)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={!selectedProduct || isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleAddProductToQuote}
                      disabled={!selectedProduct || !quantity || (selectedProductDetails && parseInt(quantity) < selectedProductDetails.minOrder) || isSubmitting}
                      startIcon={<Add />}
                    >
                      Add to Quote
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {quoteItems.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="h5" color="text.secondary" gutterBottom>
                    No items added to quote yet
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Use the form above to add products to your quote.
                  </Typography>
                </Box>
              ) : (
                <>
                  {quoteItems.map((item, index) => (
                    <motion.div
                      key={item.productId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                {item.name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                <Chip label={item.categoryName} size="small" color="secondary" variant="outlined" />
                                <Chip label={`${item.quantity} ${item.unit}`} size="small" color="primary" variant="outlined" />
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Price: {formatPrice(item.price)} per {item.unit}
                              </Typography>
                              {item.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  <strong>Notes:</strong> {item.notes}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ textAlign: 'right', ml: 2 }}>
                              <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
                                {formatPrice(item.price * item.quantity)}
                              </Typography>
                              <IconButton
                                color="error"
                                onClick={() => removeFromQuote(item.productId)}
                                size="small"
                                disabled={isSubmitting}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}

                  <Divider sx={{ my: 3 }} />
                  
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                      Total: {formatPrice(calculateTotal())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      *Final pricing may vary based on quantities and specifications
                    </Typography>
                  </Box>
                </>
              )}
            </StyledPaper>
          </motion.div>
        </Grid>

        {/* Customer Information Form */}
        <Grid item xs={12} lg={5}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <StyledPaper>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
                Customer Information
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={customerInfo.name}
                      onChange={handleCustomerInfoChange}
                      required
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={handleCustomerInfoChange}
                      required
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={customerInfo.phone}
                      onChange={handleCustomerInfoChange}
                      required
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="company"
                      value={customerInfo.company}
                      onChange={handleCustomerInfoChange}
                      variant="outlined"
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Delivery Address"
                      name="address"
                      multiline
                      rows={3}
                      value={customerInfo.address}
                      onChange={handleCustomerInfoChange}
                      variant="outlined"
                      placeholder="Enter your complete delivery address..."
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Requirements"
                      name="requirements"
                      multiline
                      rows={4}
                      value={customerInfo.requirements}
                      onChange={handleCustomerInfoChange}
                      variant="outlined"
                      placeholder="Any specific requirements, delivery timeline, or other details..."
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Button
                        type="submit"
                        variant="outlined"
                        size="large"
                        disabled={isSubmitting || quoteItems.length === 0}
                        startIcon={<WhatsApp />}
                        sx={{ flex: 1, position: 'relative' }}
                      >
                        {isSubmitting && (
                          <CircularProgress
                            size={20}
                            sx={{
                              position: 'absolute',
                              left: '50%',
                              top: '50%',
                              marginLeft: '-10px',
                              marginTop: '-10px',
                            }}
                          />
                        )}
                        {isSubmitting ? 'Submitting...' : 'Submit Quote via WhatsApp'}
                      </Button>
{/*                       
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={handleWhatsAppQuote}
                        disabled={quoteItems.length === 0 || isSubmitting}
                        startIcon={<WhatsApp />}
                        sx={{ 
                          flex: 1,
                          color: '#25D366', 
                          borderColor: '#25D366',
                          '&:hover': {
                            borderColor: '#25D366',
                            backgroundColor: 'rgba(37, 211, 102, 0.04)'
                          }
                        }}
                      >
                        WhatsApp Only
                      </Button> */}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </StyledPaper>
          </motion.div>
        </Grid>
      </Grid>
    </QuoteContainer>
  );
};

export default Inquiry;
